import type { Message } from "~/helpers/aiChat/chatStorage";
import type { AnswerRating, RatingDetails, RatingReasonCategory } from "~/services/aiOrchestrator";

import { Box, Chip, Icon, IconButton } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelButton, SubmitButton } from "~/components/Buttons";
import CheckboxField from "~/components/CheckboxField";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import DescriptionTextField from "~/components/textfield/DescriptionTextField";
import answerRating from "~/helpers/aiChat/answerRating";

interface FernieAnswerRatingProps {
	isPending: boolean;
	message: Message;
	onRate: (message: Message, picked: AnswerRating, details?: RatingDetails) => Promise<boolean>;
}

const FernieAnswerRating = ({ isPending, message, onRate }: FernieAnswerRatingProps) => {
	const { t } = useTranslation("common");
	const [isReasonOpen, setIsReasonOpen] = useState(false);
	const [reason, setReason] = useState("");
	const [category, setCategory] = useState<RatingReasonCategory | null>(null);
	const [followUpRequested, setFollowUpRequested] = useState(false);

	const categoryLabels: Record<RatingReasonCategory, string> = {
		COULD_NOT_DO: t("fernieChat.rating.categoryCouldNotDo"),
		DID_NOT_ANSWER: t("fernieChat.rating.categoryDidNotAnswer"),
		OTHER: t("fernieChat.rating.categoryOther"),
		UNHELPFUL_REDIRECT: t("fernieChat.rating.categoryUnhelpfulRedirect"),
		WRONG: t("fernieChat.rating.categoryWrong")
	};

	const pick = async (picked: AnswerRating) => {
		const isSaved = await onRate(message, picked);
		setIsReasonOpen(answerRating.shouldAskReason(isSaved, picked, message.rating));
		setReason("");
		setCategory(null);
		setFollowUpRequested(false);
	};

	const sendReason = async () => {
		if (!category) return;
		const details = { category, followUpRequested, reason: reason.trim() };
		if (await onRate(message, "DOWN", details)) setIsReasonOpen(false);
	};

	const renderThumb = (picked: AnswerRating, icon: string, label: string) => (
		<IconButton
			aria-label={label}
			aria-pressed={message.rating === picked}
			disabled={isPending}
			onClick={() => pick(picked)}
			sx={{
				...fernieButtonSx.focusRing,
				color: message.rating === picked ? "primary.main" : "text.disabled",
				height: 44,
				width: 44
			}}
		>
			<Icon sx={{ fontSize: 18 }}>{icon}</Icon>
		</IconButton>
	);

	return (
		<Box sx={{ mt: 0.5 }}>
			<Box sx={{ alignItems: "center", display: "flex" }}>
				{renderThumb("UP", "thumb_up", t("fernieChat.rating.up"))}
				{renderThumb("DOWN", "thumb_down", t("fernieChat.rating.down"))}
			</Box>
			{isReasonOpen && message.rating === "DOWN" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pb: 0.5 }}>
					<Box
						aria-label={t("fernieChat.rating.chipsLabel")}
						role="group"
						sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
					>
						{answerRating.REASON_CATEGORIES.map((reasonCategory) => (
							<Chip
								color={category === reasonCategory ? "primary" : "default"}
								key={reasonCategory}
								label={categoryLabels[reasonCategory]}
								onClick={() => setCategory(reasonCategory)}
								sx={{ minHeight: 44 }}
								variant={category === reasonCategory ? "filled" : "outlined"}
							/>
						))}
					</Box>
					{category && (
						<>
							<DescriptionTextField
								maxLength={answerRating.REASON_MAX_LENGTH}
								maxRows={4}
								onChange={(event) => setReason(event.target.value)}
								placeholder={t("fernieChat.rating.reasonPlaceholder")}
								size="small"
								slotProps={{
									htmlInput: { "aria-label": t("fernieChat.rating.reasonLabel") },
									input: { sx: { fontSize: { sm: "0.85rem", xs: "16px" } } }
								}}
								value={reason}
							/>
							<Box sx={{ minHeight: 44 }}>
								<CheckboxField
									checked={followUpRequested}
									label={t("fernieChat.rating.followUp")}
									onChange={setFollowUpRequested}
								/>
							</Box>
						</>
					)}
					<Box sx={{ display: "flex", gap: 1 }}>
						<SubmitButton
							disabled={isPending || !category}
							fullWidth={false}
							onClick={sendReason}
							size="small"
							sx={{ minHeight: 44 }}
						>
							{t("fernieChat.rating.send")}
						</SubmitButton>
						<CancelButton onClick={() => setIsReasonOpen(false)} size="small" sx={{ minHeight: 44 }}>
							{t("fernieChat.rating.skip")}
						</CancelButton>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default FernieAnswerRating;
